import {
  AdmissionStatusEnum,
  AncillaryStatusEnum,
} from "../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * What may be sent to POST /release-offers and POST /refund-offers.
 *
 * One rule FOR ADMISSIONS, shared by the cancel QUOTE (CancelBookingInitAction)
 * and the cancel COMMIT (ConfirmCancelClickAction). They used to disagree — the
 * quote skipped `REFUNDED` admissions, the commit filtered nothing at all — and
 * that drift IS the bug: on a booking whose shuttle had already been refunded (a
 * change-date cancels it automatically), the commit re-sent the dead fulfillment
 * id, the backend rejected the batch, and nothing was cancelled.
 *
 * The two paths are NOT otherwise identical, and this does not make them so:
 *   - the commit drops any offer holding an EXCHANGED/EXCHANGE_ONGOING admission
 *     (`getAllOfferWithoutExchange`); the quote filters offers by direction only;
 *   - the quote pushes ancillary ids, the commit does not — deliberately: the
 *     backend cascades an ancillary off its parent admission (verified on
 *     preprod 2026-08-07, booking 5HQ2PP: 3 admission ids in, 4 fulfillments
 *     and the full AED 180 back).
 *
 * Two conditions, both required:
 *
 *  1. `status === FULFILLED`. An allowlist, not `!== "REFUNDED"`: the status
 *     enum has 13 values (`docs/retailer.json`,
 *     BookingSnapshotFulfillmentStatus) and a denylist of one lets `RELEASED`,
 *     `EXCHANGED`, `CANCELLED`, `EXPIRED` and `ERROR` through. `RELEASED` is
 *     not hypothetical — see `shuttleIsReleasedNotRefunded`.
 *  2. Present in `booking.fulfillments`. Refunded entries are NOT removed from
 *     that array, they are re-statused, so membership alone proves nothing —
 *     but absence proves the passenger never paid.
 *
 * Mirrors `shuttleFulfillmentIds` (onwardRefund.ts), which has run this exact
 * pair on the live shuttle-refund path since it shipped.
 */
export const fulfillmentIdSet = (booking?: {
  fulfillments?: ({ id?: string } | null)[] | null;
} | null): Set<string> =>
  new Set(
    (booking?.fulfillments || [])
      .map((fulfillment) => fulfillment?.id)
      .filter((id): id is string => !!id)
  );

/**
 * Condition 1 alone — the passenger still holds this and has paid for it.
 *
 * `AdmissionStatusEnum.FULFILLED` and `AncillaryStatusEnum.FULFILLED` are the
 * same string; both are named so neither enum can be renamed out from under
 * this without the compiler noticing.
 *
 * Used on its own for ANCILLARIES, which condition 2 does not apply to: a Nol
 * pass is an ancillary and never appears in `booking.fulfillments` at all,
 * though the refund endpoints accept its id (see `nolFulfillmentIds`).
 * Requiring membership there would silently drop it from the refund.
 */
export const isFulfilledStatus = (
  item: { status?: string } | null | undefined
): boolean => {
  const status = item?.status || "";
  return (
    status === AdmissionStatusEnum.FULFILLED ||
    status === AncillaryStatusEnum.FULFILLED
  );
};

/**
 * Both conditions — for ADMISSIONS, whose id IS a fulfillment id.
 */
export const isRefundableFulfillment = (
  item: { id?: string; status?: string } | null | undefined,
  fulfillmentIds: ReadonlySet<string>
): boolean => isFulfilledStatus(item) && !!item?.id && fulfillmentIds.has(item.id);
